// MyPlanSection — customer-facing hero + plans grid for /subscriptions.
// Shows the user's current active plan (highlighted) and every available plan
// with an Upgrade / Downgrade / Current CTA. Confirming a change POSTs to
// /api/subscriptions which creates a new subscription and marks the prior one
// as "replaced" (existing backend behaviour — no server changes needed).

import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CheckCircle, ArrowUpCircle, ArrowDownCircle, Sparkles, Zap, Crown, Rocket, ShieldCheck, Loader2, CalendarClock } from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Visual accent per plan (indexed by name, falls back to slate)
const PLAN_STYLE = {
  Free:       { icon: Sparkles,   color: "slate",  gradient: "from-slate-100 to-slate-50" },
  Starter:    { icon: Zap,        color: "sky",    gradient: "from-sky-100 to-blue-50" },
  Growth:     { icon: Rocket,     color: "violet", gradient: "from-violet-100 to-indigo-50" },
  Enterprise: { icon: Crown,      color: "amber",  gradient: "from-amber-100 to-orange-50" },
};

const COLOR_CLASSES = {
  slate:  { text: "text-slate-700", border: "border-slate-300", bg: "bg-slate-100", ring: "ring-slate-400/40" },
  sky:    { text: "text-sky-700",   border: "border-sky-300",   bg: "bg-sky-100",   ring: "ring-sky-400/40" },
  violet: { text: "text-violet-700",border: "border-violet-300",bg: "bg-violet-100",ring: "ring-violet-400/40" },
  amber:  { text: "text-amber-700", border: "border-amber-300", bg: "bg-amber-100", ring: "ring-amber-400/40" },
};

function fmtINR(n) {
  const v = Number(n) || 0;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
}

function daysBetween(a, b) {
  return Math.ceil((new Date(a).getTime() - new Date(b).getTime()) / 86400000);
}

export default function MyPlanSection() {
  const { businessId } = useBusiness();
  const [mySub, setMySub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState("monthly"); // monthly | yearly
  const [pendingTarget, setPendingTarget] = useState(null); // plan to switch to (opens confirm dialog)
  const [switching, setSwitching] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/subscriptions/my-subscription${businessId ? `?business_id=${businessId}` : ""}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/subscription-plans`, { credentials: "include" }),
      ]);
      if (subRes.ok) setMySub(await subRes.json());
      if (plansRes.ok) {
        const p = await plansRes.json();
        setPlans((Array.isArray(p) ? p : p.plans || []).filter(x => x.is_active !== false));
      }
    } catch (_) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [businessId]);

  const currentPlanId = mySub?.plan?.plan_id;
  const sortedPlans = useMemo(() => {
    return [...plans].sort((a, b) => (a.monthly_price || 0) - (b.monthly_price || 0));
  }, [plans]);

  const currentPrice = mySub?.plan?.[cycle === "yearly" ? "yearly_price" : "monthly_price"] || 0;

  const compareCta = (target) => {
    if (!currentPlanId) return { label: "Choose Plan", variant: "primary", verb: "activate" };
    if (target.plan_id === currentPlanId) return { label: "Current Plan", variant: "current", verb: null };
    const targetPrice = target.monthly_price || 0;
    const cur = mySub?.plan?.monthly_price || 0;
    if (targetPrice > cur) return { label: "Upgrade", variant: "upgrade", verb: "upgrade" };
    return { label: "Downgrade", variant: "downgrade", verb: "downgrade" };
  };

  const handleSwitch = async () => {
    if (!pendingTarget || !businessId) return;
    setSwitching(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/subscriptions`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          business_id: businessId,
          plan_id: pendingTarget.plan_id,
          billing_cycle: cycle,
        }),
      });
      if (r.ok) {
        toast.success(`Switched to ${pendingTarget.name} plan`);
        setPendingTarget(null);
        fetchData();
        // Nudge the parent listing to refetch too via a custom event
        try { window.dispatchEvent(new CustomEvent("subscriptions:changed")); } catch (_) {}
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Failed (HTTP ${r.status})`);
      }
    } catch (e) {
      toast.error("Failed to switch plan");
    } finally { setSwitching(false); }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="h-40 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-64 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const style = PLAN_STYLE[mySub?.plan?.name] || PLAN_STYLE.Free;
  const HeroIcon = style.icon;
  const colors = COLOR_CLASSES[style.color];
  const expiryDate = mySub?.subscription?.expiry_date;
  const daysLeft = expiryDate ? daysBetween(expiryDate, new Date()) : null;

  return (
    <div className="space-y-5" data-testid="my-plan-section">
      {/* ─── Hero: Current Plan ─── */}
      <div className={`rounded-2xl border ${colors.border} bg-gradient-to-br ${style.gradient} dark:from-slate-800 dark:to-slate-900 dark:border-slate-700 p-5 md:p-6`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl ${colors.bg} ${colors.text} dark:bg-slate-800 flex items-center justify-center shrink-0`}>
              <HeroIcon className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Your current plan</div>
              <div className="flex items-baseline gap-3 mt-1 flex-wrap">
                <h2 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">{mySub?.plan?.name || "Free"}</h2>
                <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  mySub?.status === "active" || mySub?.status === "trial" ? "bg-emerald-100 text-emerald-700" :
                  mySub?.status === "free" ? "bg-slate-200 text-slate-700" :
                  "bg-amber-100 text-amber-700"
                }`}>
                  <ShieldCheck className="w-3 h-3" /> {mySub?.status?.toUpperCase() || "FREE"}
                </span>
              </div>
              {mySub?.plan?.tagline && (
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{mySub.plan.tagline}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-slate-900 dark:text-white">
              {currentPrice === 0 ? "Free" : fmtINR(currentPrice)}
              {currentPrice > 0 && <span className="text-sm font-normal text-slate-500">/{cycle === "yearly" ? "yr" : "mo"}</span>}
            </div>
            {daysLeft != null && daysLeft > 0 && (
              <div className="mt-1 text-xs text-slate-500 flex items-center gap-1 justify-end">
                <CalendarClock className="w-3.5 h-3.5" />
                Renews in {daysLeft} day{daysLeft === 1 ? "" : "s"}
              </div>
            )}
            {mySub?.in_grace_period && (
              <div className="mt-1 text-xs text-amber-700 font-semibold">Grace period — {mySub.grace_days_left} days left</div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Available Plans ─── */}
      <div>
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Change plan</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">Upgrade for more capacity, or downgrade to save.</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full p-1">
            <button
              onClick={() => setCycle("monthly")}
              className={`text-xs font-semibold px-3 py-1 rounded-full transition ${cycle === "monthly" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle("yearly")}
              className={`text-xs font-semibold px-3 py-1 rounded-full transition ${cycle === "yearly" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Yearly <span className="text-[10px] text-emerald-600 ml-1">save ~20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {sortedPlans.map(plan => {
            const isCurrent = plan.plan_id === currentPlanId;
            const style = PLAN_STYLE[plan.name] || PLAN_STYLE.Free;
            const c = COLOR_CLASSES[style.color];
            const Icon = style.icon;
            const cta = compareCta(plan);
            const priceNow = plan[cycle === "yearly" ? "yearly_price" : "monthly_price"] || 0;
            return (
              <div
                key={plan.plan_id}
                className={`relative bg-white dark:bg-slate-900 rounded-2xl border-2 p-5 transition ${
                  isCurrent
                    ? `${c.border} ring-4 ${c.ring}`
                    : "border-slate-200 dark:border-slate-700 hover:border-indigo-300"
                }`}
                data-testid={`plan-tile-${plan.name?.toLowerCase()}`}
              >
                {isCurrent && (
                  <div className={`absolute -top-2 left-4 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${c.bg} ${c.text}`}>
                    Current
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-xl ${c.bg} ${c.text} flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="text-base font-heading font-bold text-slate-900 dark:text-white">{plan.name}</div>
                </div>
                {plan.tagline && (
                  <p className="text-xs text-slate-500 mt-2 min-h-[32px]">{plan.tagline}</p>
                )}
                <div className="mt-4">
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    {priceNow === 0 ? "Free" : fmtINR(priceNow)}
                  </div>
                  {priceNow > 0 && (
                    <div className="text-xs text-slate-500 mt-0.5">per {cycle === "yearly" ? "year" : "month"}</div>
                  )}
                </div>

                {plan.features_display?.length > 0 && (
                  <ul className="mt-4 space-y-1.5 text-xs text-slate-700 dark:text-slate-300 min-h-[80px]">
                    {plan.features_display.slice(0, 5).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-4">
                  {cta.variant === "current" ? (
                    <Button variant="outline" disabled className="w-full" data-testid={`plan-cta-current-${plan.name?.toLowerCase()}`}>
                      <CheckCircle className="w-4 h-4 mr-1.5" /> Current Plan
                    </Button>
                  ) : cta.variant === "upgrade" ? (
                    <Button
                      onClick={() => setPendingTarget(plan)}
                      className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                      data-testid={`plan-cta-upgrade-${plan.name?.toLowerCase()}`}
                    >
                      <ArrowUpCircle className="w-4 h-4 mr-1.5" /> Upgrade
                    </Button>
                  ) : cta.variant === "downgrade" ? (
                    <Button
                      onClick={() => setPendingTarget(plan)}
                      variant="outline"
                      className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
                      data-testid={`plan-cta-downgrade-${plan.name?.toLowerCase()}`}
                    >
                      <ArrowDownCircle className="w-4 h-4 mr-1.5" /> Downgrade
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setPendingTarget(plan)}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {cta.label}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirm dialog */}
      <AlertDialog open={!!pendingTarget} onOpenChange={o => !o && setPendingTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                if (!pendingTarget || !mySub?.plan) return "Confirm plan change";
                const targetPrice = pendingTarget.monthly_price || 0;
                const cur = mySub.plan.monthly_price || 0;
                if (targetPrice > cur) return `Upgrade to ${pendingTarget.name}?`;
                if (targetPrice < cur) return `Downgrade to ${pendingTarget.name}?`;
                return `Switch to ${pendingTarget.name}?`;
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              You'll switch from <b>{mySub?.plan?.name || "Free"}</b> to <b>{pendingTarget?.name}</b> at{" "}
              <b>{pendingTarget && pendingTarget[cycle === "yearly" ? "yearly_price" : "monthly_price"] === 0 ? "Free" : fmtINR(pendingTarget?.[cycle === "yearly" ? "yearly_price" : "monthly_price"] || 0)}</b>
              /{cycle === "yearly" ? "year" : "month"}.
              {pendingTarget && mySub?.plan && (pendingTarget.monthly_price || 0) < (mySub.plan.monthly_price || 0) && (
                <div className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  Downgrading may disable modules like {Object.entries(mySub.plan.module_access || {}).filter(([k, v]) => v && !pendingTarget.module_access?.[k]).map(([k]) => k).slice(0, 3).join(", ") || "features not in the target plan"}.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={switching}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSwitch} disabled={switching} data-testid="confirm-plan-switch">
              {switching ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Switching…</> : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
